import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

interface PricingPlanProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonLink: string;
  highlighted?: boolean;
  buttonDisabled?: boolean;
  period?: string;
  featureIcon?: React.ReactNode;
  onButtonClick?: () => void;
}

const PricingPlan = ({
  title,
  price,
  description,
  features,
  buttonText,
  buttonLink,
  highlighted = false,
  buttonDisabled = false,
  period,
  featureIcon = <Check className="h-4 w-4 text-primary mr-2 flex-shrink-0" />,
  onButtonClick,
}: PricingPlanProps) => {
  return (
    <Card className={`p-4 transition-all duration-300 hover:shadow-md ${highlighted ? 'border-primary shadow-sm' : ''}`}>
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-primary">{title}</h2>
        <div className="mt-3">
          <span className="text-3xl font-bold">{price}</span>
          {period && <span className="text-muted-foreground ml-1">{period}</span>}
        </div>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>

      <ul className="space-y-2 mb-4">
        {features.map((feature, index) => {
          // Check if feature contains a colon to split into title and description
          const hasDescription = feature.includes(': ');
          const [featureTitle, featureDesc] = hasDescription ? feature.split(': ') : [feature, ''];
          
          return (
            <li key={index} className="flex items-start">
              {featureIcon}
              <div>
                <span className="text-sm font-medium">{featureTitle}</span>
                {featureDesc && (
                  <span className="block text-xs text-muted-foreground">{featureDesc}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <Button
        className={`w-full text-white ${highlighted ? 'bg-primary hover:bg-primary/90' : ''}`}
        disabled={buttonDisabled}
        onClick={onButtonClick}
        asChild={!onButtonClick && !buttonDisabled}
      >
        {buttonDisabled ? (
          <span>{buttonText}</span>
        ) : onButtonClick ? (
          <span>{buttonText}</span>
        ) : (
          <Link to={buttonLink}>{buttonText}</Link>
        )}
      </Button>
    </Card>
  );
};

export default PricingPlan;
